import React, { useEffect, useMemo, useState } from "react";
import { FaSave, FaTimes, FaUserPlus } from "react-icons/fa";
import { toast } from "react-toastify";

import UserDrawer from "./UserDrawer";
import { addUserByAdmin } from "../../../../../services/userServices";

const DEFAULT_ROLES = [
  { id: 1, nom: "Administrateur" },
  { id: 2, nom: "Utilisateur" },
  { id: 3, nom: "Enseignant" },
];

export default function AddUserDrawer({ isOpen, onClose, roles = {}, refreshList }) {
  const roleOptions = useMemo(() => {
    const entries = Object.entries(roles || {});
    if (!entries.length) return DEFAULT_ROLES;
    return entries.map(([id, nom]) => ({ id: Number(id), nom }));
  }, [roles]);

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    role_id: "",
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      nom: "",
      prenom: "",
      email: "",
      role_id: "",
    });
    setSaving(false);
  }, [isOpen]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "role_id" ? Number(value) : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      await addUserByAdmin(formData);

      toast.success("Utilisateur créé avec succès.");
      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur création utilisateur :", error);
      toast.error("Erreur lors de la création de l'utilisateur.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <UserDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Ajouter un utilisateur"
      subtitle="Créez rapidement un compte administrateur, enseignant ou utilisateur."
      width="max-w-xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-4 p-4 sm:p-6">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                <FaUserPlus />
              </span>

              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">
                  Nouveau compte
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Un mot de passe pourra être défini ensuite selon ton flux actuel.
                </p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Nom
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Prénom
                </label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Rôle
                </label>
                <select
                  name="role_id"
                  value={formData.role_id}
                  onChange={handleChange}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  <option value="">Sélectionner un rôle</option>
                  {roleOptions.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.nom}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-slate-800 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Création..." : "Créer"}
          </button>
        </div>
      </form>
    </UserDrawer>
  );
}